import React from 'react';
import { Table, Form, Select, InputNumber, Input, Button, Dropdown, Menu, Modal } from 'antd';
import { DeleteOutlined, PlusOutlined, MoreOutlined } from '@ant-design/icons';
import { getPriceForCurrency } from './invoiceHelpers';
import MaterialCard from './MaterialCard';

const { Option } = Select;

const InvoiceItemsTable = ({
  form,
  mode,
  isSale,
  materials,
  saleMaterials,
  currencies,
  factors,
  materialPrices,
  onFetchMaterials,
  onFetchSaleMaterials,
  onAddMaterial,
  onCreatePurchase,
}) => {
  const [isCardVisible, setIsCardVisible] = React.useState(false);
  const [selectedMaterialForCard, setSelectedMaterialForCard] = React.useState(null);

  // Helper function to check and auto-add new row
  const checkAndAddRow = (currentIndex) => {
    const items = form.getFieldValue('Items') || [];
    const lastItem = items[items.length - 1];

    // Check if last row has any data
    const hasLastItemData = lastItem && (
      lastItem.MaterialId ||
      lastItem.Amount ||
      lastItem.UnitPrice
    );

    // Only add if last row has data
    if (hasLastItemData || items.length === 0) {
      form.setFieldValue('Items', [...items, {}]);
    }
  };

  const getCurrencyId = () => 1; // Default to USD

  const columns = [
    {
      title: 'المادة',
      dataIndex: 'material',
      key: 'material',
      width: 180,
      render: (_, record, index) => {
        const materialId = form.getFieldValue(['Items', record.name, 'MaterialId']);
        
        const handleMenuClick = ({ key }) => {
          if (!materialId) return;
          const material = (isSale ? saleMaterials : materials).find(m => m.id === materialId);
          if (key === 'show-card') {
            setSelectedMaterialForCard(material);
            setIsCardVisible(true);
          } else if (key === 'open-movement') {
            window.open(`/material-movement-report?materialId=${materialId}`, '_blank');
          }
        };

        const menuItems = [
          { key: 'show-card', label: 'عرض بطاقة المادة', disabled: !materialId },
          { key: 'open-movement', label: 'حركة المادة', disabled: !materialId },
        ];

        const selectContent = isSale ? (
          <Form.Item
            {...record}
            name={[record.name, 'MaterialId']}
            rules={[{ required: true, message: 'المادة مطلوبة' }]}
            style={{ marginBottom: 0, padding: 0 }}
          >
            <Select
              showSearch
              filterOption={false}
              onSearch={(value) => onFetchSaleMaterials(value)}
              onChange={(value) => {
                const selectedMaterial = saleMaterials.find(m => m.id === value);
                const currencyId = getCurrencyId();
                const materialId = selectedMaterial ? selectedMaterial.id : null;
                const prices = materialPrices.find(p => p.materialId === materialId)?.prices || {};
                const priceInCurrency = getPriceForCurrency(prices, currencyId);

                form.setFieldValue(['Items', record.name, 'Currency'], currencyId);
                form.setFieldValue(['Items', record.name, 'UnitPrice'], priceInCurrency?.retailPrice ?? priceInCurrency?.unitPrice);
                form.setFieldValue(['Items', record.name, 'Amount'], 1);
                setTimeout(() => checkAndAddRow(index), 0);
              }}
              notFoundContent={
                <Button icon={<PlusOutlined />} onClick={onCreatePurchase} size="small">
                  إضافة للمستودع
                </Button>
              }
              size="small"
            >
              {(() => {
                const items = (form.getFieldValue('Items') || []);
                const currentMaterialId = items[record.name]?.MaterialId;
                const selectedMaterialIds = items
                  .map((item, idx) => idx === record.name ? null : item?.MaterialId)
                  .filter(id => id != null);

                return saleMaterials.filter(m => !selectedMaterialIds.includes(m.id) || m.id === currentMaterialId)
                  .map(material => (
                    <Option key={material.id} value={material.id} style={{width: '100%'}}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{material.name}</span>
                        <span style={{ color: '#999', fontSize: '11px' }}>
                          {material.unitPrice ?? 0}
                        </span>
                        <span style={{ fontWeight: 'bold' }}>
                          ({material.stockAmount ?? 0})
                        </span>
                      </div>
                    </Option>
                  ));
              })()}
            </Select>
          </Form.Item>
        ) : (
          <Form.Item
            {...record}
            name={[record.name, 'MaterialId']}
            rules={[{ required: true, message: 'المادة مطلوبة' }]}
            style={{ marginBottom: 0, padding: 0 }}
          >
            <Select
              showSearch
              filterOption={false}
              onSearch={(value) => onFetchMaterials(value)}
              onChange={(value) => {
                const currencyId = getCurrencyId();
                const materialId = value;
                const prices = materialPrices.find(p => p.materialId === materialId)?.prices || {};
                const priceInCurrency = getPriceForCurrency(prices, currencyId);
                form.setFieldValue(['Items', record.name, 'Currency'], currencyId);
                form.setFieldValue(['Items', record.name, 'UnitPrice'], priceInCurrency?.unitPrice);
                form.setFieldValue(['Items', record.name, 'RetailPrice'], priceInCurrency?.retailPrice);
                form.setFieldValue(['Items', record.name, 'WholesalePrice'], priceInCurrency?.wholesalePrice);
                form.setFieldValue(['Items', record.name, 'Amount'], 1);
                setTimeout(() => checkAndAddRow(index), 0);
              }}
              notFoundContent={
                <Button icon={<PlusOutlined />} onClick={onAddMaterial} size="small">
                  إضافة مادة
                </Button>
              }
              size="small"
            >
              {materials.map(material => (
                <Option key={material.id} value={material.id}>
                  {material.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
              <Button 
                type="text" 
                icon={<MoreOutlined />} 
                size="small" 
                style={{ padding: 0, margin: 0, minWidth: '20px', height: '24px' }}
              />
            </Dropdown>
            <div style={{ flex: 1 }}>
              {selectContent}
            </div>
          </div>
        );
      },
    },
    {
      title: 'الكمية',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (_, record, index) => 
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ flex: 1 }}>
            <Form.Item
              {...record}
              name={[record.name, 'Amount']}
              rules={[
                { required: true, message: 'الكمية مطلوبة' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!isSale || value === null || value === undefined || value === '') {
                      return Promise.resolve();
                    }
                    const items = getFieldValue('Items') || [];
                    const currentItem = items[record.name] || {};
                    const materialId = currentItem.MaterialId;

                    if (!materialId) {
                      return Promise.resolve();
                    }

                    const selectedMaterial = saleMaterials.find(m => m.id === materialId);
                    if (!selectedMaterial) {
                      return Promise.resolve();
                    }

                    // Skip stock validation if AllowNegativeBalance is true
                    if (selectedMaterial.allowNegativeBalance) {
                      return Promise.resolve();
                    }

                    if (value > selectedMaterial.stockAmount) {
                      return Promise.reject(new Error(`الكمية لا يمكن أن تتجاوز ${selectedMaterial.stockAmount}`));
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              style={{ marginBottom: 0, padding: 0 }}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                size="small"
                onChange={(value) => {
                  if (value) {
                    setTimeout(() => checkAndAddRow(index), 0);
                  }
                }}
              />
            </Form.Item>
          </div>
          <Form.Item
            noStyle
            dependencies={[['Items', record.name, 'MaterialId']]}
          >
            {({ getFieldValue }) => {
              const materialId = getFieldValue(['Items', record.name, 'MaterialId']);
              let unit = '';
              
              if (materialId) {
                const selectedMaterial = (isSale? saleMaterials : materials).find(m => m.id === materialId);
                unit = selectedMaterial?.unit || '';
               
              }
              
              return (
                <span style={{ color: '#666', fontSize: '10px', textAlign: 'right' }}>
                  {unit}
                </span>
              );
            }}
          </Form.Item>
        </div>
    },
    {
      title: 'سعر الوحدة',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 80,
      render: (_, record, index) => (
        <Form.Item
          {...record}
          name={[record.name, 'UnitPrice']}
          rules={[{ required: true, message: 'سعر الوحدة' }]}
          style={{ marginBottom: 0, padding: 0 }}
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            size="small"
            onChange={(value) => {
              if (value) {
                setTimeout(() => checkAndAddRow(index), 0);
              }
            }}
          />
        </Form.Item>
      ),
    },
    ...(mode === 'purchase' ? [
      {
        title: 'سعر المفرق',
        dataIndex: 'retailPrice',
        key: 'retailPrice',
        width: 100,
        render: (_, record, index) => (
          <Form.Item
            {...record}
            name={[record.name, 'RetailPrice']}
            style={{ marginBottom: 0, padding: 0 }}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              size="small"
              onChange={(value) => {
                if (value) {
                  setTimeout(() => checkAndAddRow(index), 0);
                }
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: 'سعر الجملة',
        dataIndex: 'wholesalePrice',
        key: 'wholesalePrice',
        width: 100,
        render: (_, record, index) => (
          <Form.Item
            {...record}
            name={[record.name, 'WholesalePrice']}
            style={{ marginBottom: 0, padding: 0 }}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              size="small"
              onChange={(value) => {
                if (value) {
                  setTimeout(() => checkAndAddRow(index), 0);
                }
              }}
            />
          </Form.Item>
        ),
      },
    ] : []),
    {
      title: 'العملة',
      dataIndex: 'currency',
      key: 'currency',
      width: 50,
      render: (_, record, index) => (
        <Form.Item
          {...record}
          name={[record.name, 'Currency']}
          rules={[{ required: true, message: 'العملة مفقودة' }]}
          style={{ marginBottom: 0, padding: 0 }}
        >
          <Select
            onChange={(currencyId) => {
              const items = form.getFieldValue('Items') || [];
              const currentItem = items[record.name] || {};
              const materialId = currentItem.MaterialId || null;
              const prices = materialPrices.find(p => p.materialId === materialId)?.prices || {};
              const priceInCurrency = getPriceForCurrency(prices, currencyId);

              form.setFieldValue(['Items', record.name, 'Currency'], currencyId);
              form.setFieldValue(['Items', record.name, 'UnitPrice'], priceInCurrency?.unitPrice);
              form.setFieldValue(['Items', record.name, 'RetailPrice'], priceInCurrency?.retailPrice);
              form.setFieldValue(['Items', record.name, 'WholesalePrice'], priceInCurrency?.wholesalePrice);
            }}
            size="small"
          >
            {currencies.map((c) => (
              <Option key={c.x} value={c.x}>
                {c.sympol || c.Sympol || c.symbol || c.Symbol || c.x}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: 'خصم',
      dataIndex: 'discount',
      key: 'discount',
      width: 80,
      render: (_, record, index) => (
        <Form.Item
          {...record}
          name={[record.name, 'Discount']}
          style={{ marginBottom: 0, padding: 0 }}
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            size="small"
            onChange={(value) => {
              if (value) {
                setTimeout(() => checkAndAddRow(index), 0);
              }
            }}
          />
        </Form.Item>
      ),
    },
    {
      title: 'الناتج',
      key: 'result',
      width: 100,
      render: (_, record, index) => (
        <Form.Item
          noStyle
          dependencies={[
            ['Items', record.name, 'UnitPrice'],
            ['Items', record.name, 'Amount'],
            ['Items', record.name, 'Discount'],
            ['Items', record.name, 'Currency'],
            ['Items', record.name, 'MaterialId'],
          ]}
        >
          {({ getFieldValue }) => {
            const unitPrice = getFieldValue(['Items', record.name, 'UnitPrice']) || 0;
            const amount = getFieldValue(['Items', record.name, 'Amount']) || 0;
            const discount = getFieldValue(['Items', record.name, 'Discount']) || 0;

            return (
              <span style={{ fontWeight: 'bold' }}>
                {(unitPrice * amount - discount).toFixed(2)}
              </span>
            );
          }}
        </Form.Item>
      ),
    },
    {
      title: 'ملاحظات',
      dataIndex: 'notes',
      key: 'notes',
      width: 120,
      render: (_, record, index) => (
        <Form.Item
          {...record}
          name={[record.name, 'Notes']}
          style={{ marginBottom: 0, padding: 0 }}
        >
          <Input
            size="small"
            onChange={(e) => {
              if (e.target.value) {
                setTimeout(() => checkAndAddRow(index), 0);
              }
            }}
          />
        </Form.Item>
      ),
    },
    {
      key: 'actions',
      width: 60,
      render: (_, record, index) => (
        <Button
          onClick={() => {
            const items = form.getFieldValue('Items') || [];
            const newItems = items.filter((_, i) => i !== record.name);
            form.setFieldValue('Items', newItems);
            setTimeout(() => checkAndAddRow(index), 0);
          }}
          icon={<DeleteOutlined />}
          danger
          size="small"
        />
      ),
    },
  ];

  // Initialize with empty row if needed
  React.useEffect(() => {
    checkAndAddRow();
  }, []);
  return (
    <>
      <Table
        columns={columns}
        dataSource={form.getFieldValue('Items')?.map((item, index) => ({ ...item, name: index })) || []}
        pagination={false}
        size="small"
        className="compact-items-table"
      />
      <Modal
        open={isCardVisible}
        onCancel={() => setIsCardVisible(false)}
        footer={null}
        width={600}
        title="تفاصيل المادة"
      >
        <MaterialCard
          material={selectedMaterialForCard}
          />

      </Modal>
    </>
  );
};

export default InvoiceItemsTable;
